'use client'

import { AutoAwesome } from '@mui/icons-material'
import { Drawer, Fab, Tooltip, useTheme } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import {
  ASSISTANT_BAR_HEIGHT_VAR,
  FLOATING_CHIP_BOTTOM_VAR,
  FLOATING_CHIP_RIGHT_VAR
} from '~/features/assistant/AssistantBar'
import { useCurrentOrgId } from '~/store/slices/currentOrg'
import { useMediaQuery } from '~/theme/hooks'

import AssistantPanel from './AssistantPanel'
import { ActModeProvider } from './actMode'
import { useLinkedConversation } from './conversationLink'
import { conversationRequestHandled, useOpenAssistantConversation, useRequestedConversationId } from './panelSlice'

/**
 * Space the launcher takes at the bottom of the viewport, published for the controls that float above it: the
 * medium FAB's 48px plus its 16px inset.
 */
const LAUNCHER_CLEARANCE = '64px'

/**
 * Where the internal-tool chips sit while the launcher shows: in a column to its left, 12px clear of it, with the
 * bottom chip centred on the button (a small chip is 24px tall).
 */
const FLOATING_CHIP_BOTTOM = '28px'
const FLOATING_CHIP_RIGHT = '76px'

const PANEL_WIDTH = 440

/**
 * The assistant entry point: a launcher button and the panel it opens. The panel stays mounted while closed, so
 * a reply keeps streaming behind it. A page asking for a conversation (`useOpenAssistantConversation`), or a link
 * carrying one, opens the panel on that conversation. The panel remounts per organization, so a cloak change never
 * shows another organization's conversation. The panel can widen to the whole viewport; Escape or closing it returns
 * it to its side width. Below the `sm` breakpoint it already spans the viewport, so it offers no expand control there.
 */
export default function AssistantV2() {
  const t = useTranslations('features.AssistantV2')
  const organizationId = useCurrentOrgId()
  const dispatch = useDispatch()
  const openConversation = useOpenAssistantConversation()
  const requestedConversationId = useRequestedConversationId()
  const { linkedConversationId, clear: clearLinkedConversation } = useLinkedConversation()
  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const { breakpoints } = useTheme()
  const isFullWidth = useMediaQuery(breakpoints.down('sm'))

  // A link is one more way of asking for a conversation; the parameter is dropped once the request is made.
  useEffect(() => {
    if (!linkedConversationId) return
    openConversation(linkedConversationId)
    clearLinkedConversation()
  }, [linkedConversationId, openConversation, clearLinkedConversation])

  useEffect(() => {
    if (requestedConversationId) setIsOpen(true)
  }, [requestedConversationId])

  const markRequestHandled = useCallback(() => {
    dispatch(conversationRequestHandled())
  }, [dispatch])

  useEffect(() => {
    if (isOpen) return undefined
    const root = document.documentElement
    root.style.setProperty(ASSISTANT_BAR_HEIGHT_VAR, LAUNCHER_CLEARANCE)
    root.style.setProperty(FLOATING_CHIP_BOTTOM_VAR, FLOATING_CHIP_BOTTOM)
    root.style.setProperty(FLOATING_CHIP_RIGHT_VAR, FLOATING_CHIP_RIGHT)
    return () => {
      root.style.removeProperty(ASSISTANT_BAR_HEIGHT_VAR)
      root.style.removeProperty(FLOATING_CHIP_BOTTOM_VAR)
      root.style.removeProperty(FLOATING_CHIP_RIGHT_VAR)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !isExpanded) return undefined
    const onKeyDown = (event: KeyboardEvent) => {
      // A key the panel already handled, such as Escape closing the mention picker, leaves the size alone.
      if (event.key === 'Escape' && !event.defaultPrevented) setIsExpanded(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isExpanded, isOpen])

  const close = () => {
    setIsOpen(false)
    setIsExpanded(false)
  }
  const fillsViewport = isExpanded && !isFullWidth

  return (
    <ActModeProvider>
      {!isOpen && (
        <Tooltip
          title={t('open')}
          placement="top"
          // Lifted clear of the internal-tool chips, whose column tops out 20px above the button.
          slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, 8] } }] } }}
        >
          <Fab
            color="primary"
            size="medium"
            aria-label={t('open')}
            onClick={() => setIsOpen(true)}
            sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: (theme) => theme.zIndex.speedDial }}
          >
            <AutoAwesome />
          </Fab>
        </Tooltip>
      )}
      <Drawer
        anchor="right"
        variant="persistent"
        open={isOpen}
        slotProps={{
          paper: {
            'aria-label': t('title'),
            sx: {
              width: fillsViewport ? '100vw' : { xs: '100%', sm: PANEL_WIDTH },
              zIndex: (theme) => theme.zIndex.drawer + 2
            }
          }
        }}
      >
        <AssistantPanel
          key={organizationId}
          organizationId={organizationId}
          linkedConversationId={requestedConversationId}
          onLinkedConversationOpened={markRequestHandled}
          onClose={close}
          isExpanded={fillsViewport}
          onToggleExpanded={isFullWidth ? undefined : () => setIsExpanded((value) => !value)}
        />
      </Drawer>
    </ActModeProvider>
  )
}
